import React, { createContext, useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import {
  getDomainList as fetchDomainList,
  getDomainDashboardSummary as fetchDomainDashboardSummary,
  addDomain as addDomainService,
  uploadDomain,
  whitelistDomains as whitelistDomainsService,
  blacklistDomains as blacklistDomainsService,
} from "../../../../services/S&OP/S&OPServices";
import FailedDomainsToast from "./FailedDomainsToast";
import {
  TOAST_FETCH_DOMAIN_ERROR,
  TOAST_FETCH_SUMMARY_ERROR,
  TOAST_UPLOAD_SUCCESS,
  TOAST_UPLOAD_ERROR,
  TOAST_ADD_DOMAIN_ERROR,
  TOAST_WHITELIST_SUCCESS,
  TOAST_WHITELIST_ERROR,
  TOAST_REMOVE_SUCCESS,
  TOAST_REMOVE_ERROR,
} from "./constants/manageWhitelistDomainText";

const ManageWhitelistDomainContext = createContext(null);

export const ManageWhitelistDomainProvider = ({ children }) => {
  const [rowData, setRowData] = useState([]);
  const [summary, setSummary] = useState({
    totalDomains: 0,
    domainsWhitelisted: 0,
    domainsOnboarded: 0,
    pendingWhitelist: 0,
    pendingOnboard: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const getDomainList = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setTableLoading(true);
      const list = await fetchDomainList() || [];

      setRowData(
        list.map((item) => ({
          alIdentifier: item.id,
          requestNo: item.requestId,
          ticketUrl: item.ticketUrl || "",
          domain: item.domainName,
          applicationType: item.applicationType || "",
          requestedBy: item.requestedBy,
          email: item.requestedByEmail || "",
          requestDate: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB")
            : "",
          status: item.status,
        }))
      );
    } catch {
      toast.error(TOAST_FETCH_DOMAIN_ERROR);
    } finally {
      setSummaryLoading(false);
      setTableLoading(false);
    }
  }, []);

  const getDashboardSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const data = await fetchDomainDashboardSummary();
      setSummary(data || {});
    } catch {
      toast.error(TOAST_FETCH_SUMMARY_ERROR);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const onUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setSummaryLoading(true);
    setTableLoading(true);
    try {
      const result = await uploadDomain(formData);

      if (result?.failedCount > 0) {
        const toastId = "upload-partial-failure";
        toast.warn(
          <FailedDomainsToast
            toastId={toastId}
            successCount={result.successCount}
            failedCount={result.failedCount}
            failedDomains={result.failedDomains ?? []}
          />,
          { toastId, autoClose: false, closeOnClick: false }
        );
      } else {
        toast.success(TOAST_UPLOAD_SUCCESS(result?.successCount));
      }

      getDomainList();
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.message || TOAST_UPLOAD_ERROR, { autoClose: 2000 });
      setSummaryLoading(false);
      setTableLoading(false);
    }
  }, [getDomainList]);

  const addDomain = useCallback(async (data) => {
    setSummaryLoading(true);
    setTableLoading(true);
    try {
      const result = await addDomainService(data);

      if (result?.success) {
        toast.success(result.message, { autoClose: 2000 });
        getDashboardSummary();
        getDomainList();
      } else {
        toast.error(result?.message, { autoClose: 2000 });
        setSummaryLoading(false);
        setTableLoading(false);
      }
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.message || TOAST_ADD_DOMAIN_ERROR, { autoClose: 2000 });
      setSummaryLoading(false);
      setTableLoading(false);
    }
  }, [getDomainList]);

  const whitelistDomains = useCallback(async (payload) => {
    setSummaryLoading(true);
    setTableLoading(true);
    try {
      const result = await whitelistDomainsService(payload);
      toast.success(result?.message || TOAST_WHITELIST_SUCCESS, { autoClose: 2000 });
      getDomainList();
      getDashboardSummary();
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.message || TOAST_WHITELIST_ERROR, { autoClose: 2000 });
      setSummaryLoading(false);
      setTableLoading(false);
    }
  }, [getDomainList, getDashboardSummary]);

  const blacklistDomains = useCallback(async (payload) => {
    setSummaryLoading(true);
    setTableLoading(true);
    try {
      const result = await blacklistDomainsService(payload);
      toast.success(result?.message || TOAST_REMOVE_SUCCESS, { autoClose: 2000 });
      getDomainList();
      getDashboardSummary();
    } catch (error) {
      const res = error?.response?.data;
      toast.error(res?.message || TOAST_REMOVE_ERROR, { autoClose: 2000 });
      setSummaryLoading(false);
      setTableLoading(false);
    }
  }, [getDomainList, getDashboardSummary]);

  return (
    <ManageWhitelistDomainContext.Provider
      value={{
        rowData,
        summary,
        tableLoading,
        summaryLoading,
        getDomainList,
        getDashboardSummary,
        onUpload,
        addDomain,
        whitelistDomains,
        blacklistDomains,
      }}
    >
      {children}
    </ManageWhitelistDomainContext.Provider>
  );
};

export const useManageWhitelistDomain = () => {
  const context = useContext(ManageWhitelistDomainContext);
  if (!context) {
    throw new Error("useManageWhitelistDomain must be used within ManageWhitelistDomainProvider");
  }
  return context;
};
